import type { RouteRecordRaw } from 'vue-router'
import { useSessionStore } from '@/apps/session/stores/session'
import type { OrganizationGate } from '@/entries/app/organizationGateGuard'
import { PublicFlags, type PublicFlag } from '@/types/permissions'
import { loadOrgExportHistory } from './exportHistory'

/**
 * A route open only in an organization carrying a public flag, as `GET /access` resolved it for
 * the organization in the URL (`org.features`). It goes in the route's `meta.gate`, which the
 * organization gate guard asks on entering the route and again whenever the organization in the
 * URL changes; the session guard has read that organization's answer by then, so this reads a
 * store and makes no request. A refused navigation lands on the organization's home, as the
 * permission guard's does.
 *
 * **Ergonomics, not a control**: the backend refuses every read such a screen makes in an
 * organization without the flag. What this spares is a screen that can only fail.
 */
export function requiresOrgFeature(flag: PublicFlag): OrganizationGate {
  return () => useSessionStore().hasOrgFeature(flag)
}

/**
 * A route open only where the organization's platform **sells credits** — `GET /access`'s
 * `org.sells_credits` (`docs/plans/billing-ledger.md` D17). A tenant billed by contract beside
 * the product is metered, never charged and never refused — and a platform nobody provisioned
 * sells nothing either — so a wallet, a top-up, a journal, a purchase history and an invoice are
 * all affordances against a balance that does not exist and cannot be filled.
 *
 * **{@link requiresOrgFeature}'s sibling and not a call into it**, because the carrier is a field
 * of its own rather than a public flag: the credit regime is configuration, and a flag beside the
 * configuration would be a second source for one truth.
 *
 * **Ergonomics, not a control**, exactly as above: the backend opens no wallet for such a tenant,
 * so every read behind these screens answers nothing whether or not this gate is asked.
 */
export function requiresCreditSales(): OrganizationGate {
  return () => useSessionStore().sellsCredits()
}

/**
 * The exports page's gate: open where `assets.library_exports` is on and, without it, only to an
 * organization that has exported before (`./exportHistory.ts`), which it asks once and keeps in the
 * organization's query cache — the one request a gate makes.
 */
const exportsPageGate: OrganizationGate = (organizationId, { queryClient }) =>
  useSessionStore().hasOrgFeature(PublicFlags.ASSETS_LIBRARY_EXPORTS) ||
  loadOrgExportHistory(queryClient, organizationId)

/**
 * The settings routes Videas adds to the shared settings screens.
 *
 * **The money is served since 2026-09-20** (`docs/plans/billing-ledger.md` B7c), and since
 * 2026-10-13 it is **one page in four tabs** — overview, consumption, history, preferences —
 * rather than six screens (`docs/plans/billing-settings.md`). Every tab asks
 * `tenancy.billing.manage`, reads included — the backend's own gate on all its operations — which
 * is a change of audience for the consumption, open to any member while it was Django's bandwidth
 * report. The six screens' addresses redirect to their tab, because mails already sent link to
 * them.
 *
 * **Payment methods are not registered and `BillingPaymentMethodsView` is deleted with the Django
 * list it rendered.** The invoicing service keeps one card per customer, kept by paying a top-up
 * with it and replaced by the next (`docs/plans/billing-ledger.md` B8), so there is no list to
 * draw: the wallet screen's automatic top-up section says whether one is kept and offers the
 * top-up that keeps another.
 *
 * The media players (`OrgPresetsView`) are served: the watermark is the organization's settings
 * pair and the presets their own resource (`docs/plans/embeds-rust.md` ER1).
 *
 * The IDEO cards (`IdeoCardsView`) are served, to any member of an organization carrying
 * `onisep.enabled` — which is the whole of what the catalogue's four reads ask
 * (`docs/plans/onisep-rust.md` O8).
 *
 * The ONISEP partners (`PartnersView`) are served on the same flag, and read by any member; its
 * writes are `onisep.partners.manage`'s, which the screen gates its buttons on (O8, P4).
 *
 * The workspaces (`WorkspacesView`) are every workspace of the organization with its owner, its
 * seats, its storage and its last placement, for a holder of `tenancy.workspaces.view_all` or
 * `.manage_all` — either key reaches every workspace, and the route takes one of them.
 *
 * The exports (`ExportsView`) are `assets.library.export`'s and stand behind **no** flag gate:
 * the organization's export log is read whether or not `assets.library_exports` is on, and the screen
 * hides its form where it is off (`docs/plans/organization-exports.md` E8, E10). Without the flag
 * the route is open only to an organization that has exported before (`./exportHistory.ts`): one
 * that never had exports would land on a notice over an empty log.
 *
 * The caller's notifications (`NotificationsView`) are every member's, as their route is: each reads
 * and writes their own choices, and sees only the kinds that may reach them. Its address is the one
 * every notification mail links to (`videas_core::back_office::notification_settings_path`), so it
 * does not move without that constant. The organization's defaults (`NotificationDefaultsView`) are
 * `tenancy.settings.manage`'s, the key their read and write take (`docs/plans/notifications.md`
 * N3b).
 */
/** What every money screen asks for. The route's own gate is the backend's, read off the handler. */
const BILLING: RouteRecordRaw['meta'] = {
  requiresAuth: true,
  requires: { permissions: 'tenancy.billing.manage' },
}

/**
 * What three of the four money tabs add to it: they are unreachable on a platform that sells no
 * credits (D17). The consumption carried it from 2026-09-24 and **no longer does**: the owner's U9
 * (2026-10-04) shows a contract tenant its volumes, with no amount and no wallet.
 */
const SELLING_ONLY = requiresCreditSales()

export const videasSettingsRoutes: RouteRecordRaw[] = [
  // The money is one page in four tabs (`docs/plans/billing-settings.md` S1): the frame carries the
  // header and the tabs, each tab a route of its own so it is linkable. The frame asks what every
  // money screen asks; three tabs add SELLING_ONLY, the consumption does not (S3).
  {
    path: 'billing',
    component: () => import('./views/BillingView.vue'),
    meta: BILLING,
    children: [
      {
        path: '',
        name: 'settings:billing',
        component: () => import('./views/BillingOverviewView.vue'),
        meta: {
          title: 'Billing',
          ...BILLING,
          // A platform billed by contract has no wallet but has the consumption tab: the address the
          // mails and the home card link to lands there rather than on the organization's home.
          gate: (organizationId) =>
            useSessionStore().sellsCredits() || { name: 'settings:billing-usage', params: { organizationId } },
        },
      },
      {
        path: 'usage',
        name: 'settings:billing-usage',
        component: () => import('./views/BandwidthUsageView.vue'),
        meta: { title: 'Consumption', ...BILLING },
      },
      {
        path: 'history',
        name: 'settings:billing-history',
        component: () => import('./views/BillingHistoryView.vue'),
        meta: { title: 'Billing history', ...BILLING, gate: SELLING_ONLY },
      },
      {
        path: 'preferences',
        name: 'settings:billing-preferences',
        component: () => import('./views/BillingPreferencesView.vue'),
        meta: { title: 'Billing preferences', ...BILLING, gate: SELLING_ONLY },
      },
      // The former screens' addresses (S8). Every redirect keeps the query; the names stay so a
      // link naming one still resolves.
      // The top-ups' address is in every renewal mail (`?pay=`) and was a bank's return
      // (`?purchase=`); the history opens both, so the query goes along whole.
      {
        path: 'purchases',
        name: 'settings:billing-purchases',
        redirect: (to) => ({ name: 'settings:billing-history', query: to.query }),
      },
      {
        path: 'documents',
        name: 'settings:billing-documents',
        redirect: (to) => ({ name: 'settings:billing-history', query: { ...to.query, view: 'documents' } }),
      },
      {
        path: 'credits',
        name: 'settings:billing-credits',
        redirect: (to) => ({ name: 'settings:billing-history', query: to.query }),
      },
      {
        path: 'identity',
        name: 'settings:billing-identity',
        redirect: (to) => ({ name: 'settings:billing-preferences', query: to.query }),
      },
    ],
  },
  // The consumption's former address, which the allowance mail sent until 2026-10-13.
  {
    path: 'usage',
    name: 'settings:organization-usage',
    redirect: (to) => ({ name: 'settings:billing-usage', query: to.query }),
  },
  {
    path: 'workspaces',
    name: 'settings:organization-workspaces',
    component: () => import('./views/WorkspacesView.vue'),
    meta: {
      title: 'Workspaces',
      requiresAuth: true,
      requires: { anyPermissions: ['tenancy.workspaces.view_all', 'tenancy.workspaces.manage_all'] },
    },
  },
  {
    path: 'exports',
    name: 'settings:organization-exports',
    component: () => import('./views/ExportsView.vue'),
    meta: {
      title: 'Exports',
      requiresAuth: true,
      requires: { permissions: 'assets.library.export' },
      gate: exportsPageGate,
    },
  },
  {
    path: 'notifications',
    name: 'settings:notifications',
    component: () => import('./views/NotificationsView.vue'),
    meta: { title: 'Notifications', requiresAuth: true },
  },
  {
    path: 'notification-defaults',
    name: 'settings:organization-notification-defaults',
    component: () => import('./views/NotificationDefaultsView.vue'),
    meta: {
      title: 'Default notifications',
      requiresAuth: true,
      requires: { permissions: 'tenancy.settings.manage' },
    },
  },
  {
    path: 'media-players',
    name: 'settings:organization-media-players',
    component: () => import('./views/OrgPresetsView.vue'),
    meta: {
      title: 'Media Players',
      requiresAuth: true,
      requires: { permissions: 'tenancy.settings.manage' },
    },
  },
  {
    path: 'partners',
    name: 'settings:organization-partners',
    component: () => import('./views/PartnersView.vue'),
    meta: { title: 'Partners', requiresAuth: true, gate: requiresOrgFeature(PublicFlags.ONISEP_ENABLED) },
  },
  {
    path: 'ideo-cards',
    name: 'settings:organization-ideo-cards',
    component: () => import('./views/IdeoCardsView.vue'),
    meta: { title: 'Ideo Cards', requiresAuth: true, gate: requiresOrgFeature(PublicFlags.ONISEP_ENABLED) },
  },
  {
    path: 'advertising',
    name: 'settings:organization-advertising',
    component: () => import('./views/ComingSoonView.vue'),
    meta: { title: 'Advertising', requiresAuth: true },
  },
  {
    path: 'advanced',
    name: 'settings:organization-advanced',
    component: () => import('./views/ComingSoonView.vue'),
    meta: { title: 'Advanced Settings', requiresAuth: true },
  },
]
